import { type ChangeEvent, type ReactElement, cloneElement, useEffect, useRef, useState } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const TreeSearch = ({
  value,
  onValueChange,
  onClear,
  placeholder = 'Search',
  isLoading = false,
  className,
  inputClassName,
  toggleElement,
  disabled = false,
}: {
  value: string;
  onValueChange: (value: string) => void;
  onClear: () => void;
  placeholder?: string;
  isLoading?: boolean;
  className?: string;
  inputClassName?: string;
  toggleElement?: React.ReactNode;
  disabled?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const tTree = useTranslations('tree');

  useEffect(() => {
    if (expanded) {
      inputRef.current?.focus();
    }
  }, [expanded]);

  useEffect(() => {
    if (value.length > 0) {
      setExpanded(true);
    }
  }, [value]);

  const handleToggle = () => {
    setExpanded(true);
  };

  const handleClear = () => {
    onClear();
    setExpanded(false);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onValueChange(event.target.value);
  };

  return (
    <div className={cn('flex w-full items-center', className)}>
      {expanded ? (
        <Input
          prependIcon={<Search className="mr-2 icon-6 text-icon-base" strokeWidth={2} />}
          appendIcon={
            isLoading ? (
              <Loader2 className="ml-2 icon-6 animate-spin text-icon-base" strokeWidth={2} />
            ) : (
              <X className="ml-2 icon-6 text-icon-base" strokeWidth={2} onClick={handleClear} />
            )
          }
          safeInputRef={inputRef}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          inputFieldClassName="text-lg font-extralight text-ellipsis"
          className={cn(
            'flex h-8 w-full flex-1 items-center  rounded-full border-0 border-black/25 bg-input-background px-3 shadow-none ring-1 ring-ui-bevel transition duration-150 focus-within:outline-hidden focus-within:ring-2 focus-within:ring-ui-separator focus-within:ring-offset-0 focus-within:ring-offset-ui-panel focus-within:duration-150 dark:border-white/25',
            inputClassName,
          )}
          disabled={disabled}
          data-cy="toolbar-search-input"
        />
      ) : (
        cloneElement(
          toggleElement ? (
            (toggleElement as ReactElement)
          ) : (
            <Button
              variant="main-toolbar"
              size="main-toolbar"
              className="icon-8 p-0"
              disabled={disabled}
              icon={Search}
              data-cy="tree-search-toggle"
              tooltip={tTree('searchTooltip')}
              tooltipClassName="max-w-29 text-balance text-center"
            />
          ),
          { onClick: handleToggle },
        )
      )}
    </div>
  );
};
